import React, { useEffect, useCallback, useState } from 'react'
import PropTypes from 'prop-types'
import gql from 'graphql-tag'
import { useQuery } from 'react-apollo'

import routes from 'src/lib/rails-routes/rails-routes'
import { post } from 'lib/http'
import { t } from 'src/lib/i18n-react'
import ActivityWrapup from 'src/pages/ActivityWrapup/ActivityWrapup'
import ActivityWrapupHeader from 'src/pages/ActivityWrapup/subcomponents/ActivityWrapupHeader/ActivityWrapupHeader'
import ActivityWrapupPlaceholder from 'src/pages/ActivityWrapup/ActivityWrapupPlaceholder'
import AppearanceAnimation from 'src/ui/AppearanceAnimation/AppearanceAnimation'
import DataFetchError, {
  ERROR_CODE_500,
} from 'src/ui/DataFetchError/DataFetchError'
import {
  getGoalReachedState,
  shouldShowCelebrations,
  hasUserAlreadySeenCelebration,
  persistCelebratedGoal,
} from 'src/pages/ActivityWrapup/utils/celebrations'
import ActivityWrapupGoalCelebration from 'src/pages/ActivityWrapup/ActivityWrapupGoalCelebration'

const GET_PROGRESS = gql`
  query GET_PROGRESS($l2Code: LanguageEnum!) {
    currentUser {
      progress(code: $l2Code) {
        weeklyGoals(kind: minutes) {
          completed: progress
          goal: final
          completeFraction: percent
        }
      }
    }
  }
`

const REQUEST_ROUTE_BY_ACTIVITY = {
  reading: routes.language_reading_update_rating,
  video: routes.language_media_update_rating,
  audio: routes.language_media_update_rating,
}

const ActivityWrapupLazy = ({
  l2Code,
  activityType,
  subtitle,
  gid,
  correctionsRequested,
  showLiveLessonSection,
  mediaThumbnailUrl,
  practiceWritingHref,
  vocabularyGroups,
  onRenderHeader,
  onShowSnackbar,
}) => {
  // `initial`, `visible`, `finished`, `skipped`
  const [celebrationsState, setCelebrationsState] = useState('initial')

  const progressResponse = useQuery(GET_PROGRESS, {
    variables: {
      l2Code,
    },
    fetchPolicy: 'network-only',
  })

  const weeklyGoals = progressResponse?.data?.currentUser?.progress?.weeklyGoals

  useEffect(() => {
    if (celebrationsState === 'initial' && !weeklyGoals) {
      // loading
      onRenderHeader(
        <ActivityWrapupHeader activityType={activityType} onlyLogo />
      )
      return
    }

    if (celebrationsState === 'visible') {
      // same header as during loading
      return
    }

    if (celebrationsState === 'finished' || celebrationsState === 'skipped') {
      // an activity header
      onRenderHeader(
        <AppearanceAnimation>
          <ActivityWrapupHeader activityType={activityType} />
        </AppearanceAnimation>
      )
      return
    }

    const goalReached = getGoalReachedState(weeklyGoals)

    if (
      shouldShowCelebrations(goalReached) &&
      !hasUserAlreadySeenCelebration(goalReached)
    ) {
      // show celebrations screen
      setCelebrationsState('visible')
      persistCelebratedGoal(goalReached)
      return
    }

    // show wrapup screen
    setCelebrationsState('skipped')
  }, [
    activityType,
    onRenderHeader,
    weeklyGoals,
    celebrationsState,
    setCelebrationsState,
  ])

  const handleContinueAfterCelebrations = useCallback(() => {
    // show wrapup screen
    setCelebrationsState('finished')
  }, [setCelebrationsState])

  const handleRateActivity = useCallback(
    async (rating) => {
      try {
        // TODO: create graphQL mutation
        const response = await post(
          REQUEST_ROUTE_BY_ACTIVITY[activityType]({
            code: l2Code,
            gid,
          }),
          {
            rating,
          },
          'json'
        )
        if (response?.data?.status !== 'ok') {
          throw Error('Request failed')
        }
        window.eventTracking?.track?.('rating', rating, activityType)
      } catch (error) {
        console.error(error)
        onShowSnackbar({
          message: t('Rating was not saved. Please try again later.'),
          type: 'error',
        })
      }
    },
    [l2Code, activityType, gid, onShowSnackbar]
  )

  if (progressResponse.loading || celebrationsState === 'initial') {
    return <ActivityWrapupPlaceholder />
  }

  if (activityType) {
    if (celebrationsState === 'visible') {
      return (
        <ActivityWrapupGoalCelebration
          activityType={activityType}
          weeklyGoals={weeklyGoals}
          goalReached={getGoalReachedState(weeklyGoals)}
          onContinue={handleContinueAfterCelebrations}
        />
      )
    }

    return (
      <AppearanceAnimation disabled={celebrationsState === 'skipped'}>
        <ActivityWrapup
          activityType={activityType}
          subtitleSection={subtitle}
          correctionsRequested={correctionsRequested}
          showLiveLessonSection={showLiveLessonSection}
          mediaThumbnailUrl={mediaThumbnailUrl}
          practiceWritingHref={practiceWritingHref}
          vocabularyGroups={vocabularyGroups}
          weeklyGoals={weeklyGoals}
          onActivityRated={handleRateActivity}
        />
      </AppearanceAnimation>
    )
  }

  return <DataFetchError errorCode={ERROR_CODE_500} />
}

ActivityWrapupLazy.propTypes = {
  l2Code: PropTypes.string.isRequired,
  activityType: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  gid: PropTypes.string,
  correctionsRequested: PropTypes.bool,
  showLiveLessonSection: PropTypes.bool,
  // showOnDemandSection: PropTypes.bool, TODO: implement in https://github.com/chatterbugapp/chatterbug/issues/3712
  mediaThumbnailUrl: PropTypes.string,
  practiceWritingHref: PropTypes.string.isRequired,
  vocabularyGroups: PropTypes.arrayOf(PropTypes.object),
  onRenderHeader: PropTypes.func.isRequired,
  onShowSnackbar: PropTypes.func,
}

export default ActivityWrapupLazy
